<template>
  <div :class="$style.gameExplorer">
    <button
      :class="$style.exploreTrigger"
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      aria-controls="explore-games-panel"
      @click="isOpen = !isOpen"
    >
      <span :class="$style.triggerIcon" aria-hidden="true">✦</span>
      Explore games
    </button>

    <div
      v-if="isOpen"
      :class="$style.exploreBackdrop"
      aria-hidden="true"
      @click="isOpen = false"
    />
    <section
      v-if="isOpen"
      id="explore-games-panel"
      :class="$style.explorePanel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="explore-games-title"
    >
      <button
        :class="$style.panelClose"
        type="button"
        aria-label="Close explore games"
        @click="isOpen = false"
      >
        ×
      </button>
      <p :class="$style.eyebrow">More from iMapBook</p>
      <h2 id="explore-games-title">Explore games</h2>
      <p :class="$style.panelIntro">Different ways to learn, discuss, and play together.</p>

      <div :class="$style.gameList">
        <article :class="[$style.gameCard, $style.saboteurGame]">
          <span :class="[$style.gameMark, $style.saboteurMark]" aria-hidden="true">S</span>
          <div>
            <div :class="$style.gameCardHeading">
              <h3>Saboteur</h3>
              <span>Current game</span>
            </div>
            <p>A hidden-role game of teamwork, suspicion, and clever sabotage.</p>
          </div>
        </article>

        <a
          :class="[$style.gameCard, $style.timelineGame, $style.timelineGameLink]"
          :href="timelineUrl"
          @click="isOpen = false"
        >
          <span :class="[$style.gameMark, $style.timelineMark]" aria-hidden="true">T</span>
          <div>
            <h3>Timeline</h3>
            <p>Build a visual timeline by matching story moments to their place in the narrative.</p>
            <span :class="$style.timelineLink">Explore Timeline <span aria-hidden="true">→</span></span>
          </div>
        </a>
      </div>
    </section>
  </div>
</template>

<script>
const defaultTimelineUrl = "https://timeline.imapbook.com/";

export default {
  name: "ExploreGamesLauncher",
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    timelineUrl() {
      const candidate = process.env.VUE_APP_TIMELINE_URL?.trim() || defaultTimelineUrl;

      try {
        const url = new URL(candidate);
        return ["http:", "https:"].includes(url.protocol) ? url.href : defaultTimelineUrl;
      } catch {
        return defaultTimelineUrl;
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    handleKeydown(event) {
      if (event.key === "Escape") this.isOpen = false;
    },
  },
};
</script>

<style module>
.gameExplorer {
  position: fixed;
  right: max(18px, env(safe-area-inset-right));
  bottom: max(18px, env(safe-area-inset-bottom));
  z-index: 60;
}

.exploreTrigger {
  align-items: center;
  background: #112d4e;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(17, 45, 78, 0.25);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  gap: 8px;
  padding: 11px 16px;
}

.exploreTrigger:hover,
.exploreTrigger:focus-visible {
  background: #1d4a7a;
  outline: 3px solid #a9caee;
  outline-offset: 3px;
}

.triggerIcon {
  color: #f6cf7a;
  font-size: 1.1rem;
  line-height: 1;
}

.exploreBackdrop {
  position: fixed;
  inset: 0;
  background: rgba(11, 28, 48, 0.28);
}

.explorePanel {
  position: absolute;
  right: 0;
  bottom: 58px;
  width: min(370px, calc(100vw - 36px));
  background: #fff;
  border: 1px solid #cbd9ea;
  border-radius: 20px;
  box-shadow: 0 22px 60px rgba(17, 45, 78, 0.27);
  color: #112d4e;
  padding: 24px;
}

.panelClose {
  position: absolute;
  top: 11px;
  right: 13px;
  background: transparent;
  border: 0;
  color: #526b86;
  cursor: pointer;
  font-size: 1.55rem;
  line-height: 1;
  padding: 4px;
}

.panelClose:hover,
.panelClose:focus-visible {
  color: #112d4e;
  outline: 2px solid #a9caee;
  outline-offset: 1px;
}

.eyebrow {
  color: #3f72af;
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  margin: 0 0 4px;
  text-transform: uppercase;
}

.explorePanel h2 {
  font-family: "Kavoon", serif;
  font-size: 1.7rem;
  margin: 0;
}

.panelIntro {
  color: #53677c;
  font-size: 0.92rem;
  line-height: 1.45;
  margin: 7px 0 18px;
}

.gameList {
  display: grid;
  gap: 10px;
}

.gameCard {
  align-items: flex-start;
  border: 1px solid #d8e2ef;
  border-radius: 14px;
  display: grid;
  gap: 12px;
  grid-template-columns: 42px minmax(0, 1fr);
  padding: 13px;
}

.saboteurGame {
  background: #fff9ed;
}

.timelineGame {
  background: #f5f9ff;
}

.timelineGameLink {
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.timelineGameLink:hover,
.timelineGameLink:focus-visible {
  border-color: #3f72af;
  box-shadow: 0 7px 16px rgba(17, 45, 78, 0.14);
  outline: none;
  transform: translateY(-2px);
}

.gameMark {
  align-items: center;
  border-radius: 12px;
  display: inline-flex;
  font-family: "Kavoon", serif;
  font-size: 1.35rem;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.saboteurMark {
  background: #4c3327;
  color: #f6cf7a;
}

.timelineMark {
  background: #dbeafa;
  color: #1d4a7a;
}

.gameCard h3 {
  font-size: 1rem;
  margin: 0;
}

.gameCard p {
  color: #53677c;
  font-size: 0.82rem;
  line-height: 1.4;
  margin: 4px 0 0;
}

.gameCardHeading {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.gameCardHeading span {
  color: #92502f;
  font-size: 0.67rem;
  font-weight: 800;
}

.timelineLink {
  color: #1d4a7a;
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 900;
  margin-top: 9px;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

@media (max-width: 560px) {
  .gameExplorer {
    right: 14px;
    bottom: 14px;
  }

  .explorePanel {
    bottom: 54px;
    padding: 21px;
  }

  .exploreTrigger {
    padding: 10px 14px;
  }
}
</style>
